/**
 * SupplierCategoryPicker
 * ----------------------
 * Multi-select category picker used in the Add/Edit Supplier forms.
 *
 * - Renders all curated categories from /data/supplierCategories.js as toggle
 *   chips. Click to toggle.
 * - Allows free-text "custom" tags: type → press Enter / comma to add. Custom
 *   tags render with a slightly different style and a tiny "✕" remove button.
 * - `value`  : string[]  (mix of curated ids and custom strings)
 * - `onChange`: (next: string[]) => void
 */

import { useState } from "react";
import { Check, X, Tag } from "lucide-react";
import { SUPPLIER_CATEGORIES, isCuratedCategory, labelForCategory } from "@/data/supplierCategories";

export default function SupplierCategoryPicker({ value, onChange, allowCustom = true, testId = "supplier-category-picker" }) {
  const selected = Array.isArray(value) ? value : [];
  const [draft, setDraft] = useState("");

  const toggle = (id) => {
    if (selected.includes(id)) {
      onChange(selected.filter((x) => x !== id));
    } else {
      onChange([...selected, id]);
    }
  };

  const addCustom = () => {
    const raw = draft.trim().toLowerCase().replace(/\s+/g, "_").slice(0, 40);
    if (!raw) return;
    if (selected.includes(raw)) {
      setDraft("");
      return;
    }
    onChange([...selected, raw]);
    setDraft("");
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addCustom();
    }
  };

  const customSelected = selected.filter((s) => !isCuratedCategory(s));

  return (
    <div data-testid={testId} className="space-y-3">
      {/* Curated chip grid */}
      <div className="flex flex-wrap gap-2">
        {SUPPLIER_CATEGORIES.map((c) => {
          const on = selected.includes(c.id);
          return (
            <button
              type="button"
              key={c.id}
              onClick={() => toggle(c.id)}
              title={c.description}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-semibold transition-colors border ${
                on
                  ? "bg-amber-600 text-white border-amber-600 shadow-sm"
                  : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
              }`}
              data-testid={`cat-chip-${c.id}`}
            >
              {on && <Check className="w-3 h-3" />}
              {c.label}
            </button>
          );
        })}
      </div>

      {/* Custom tag row */}
      {customSelected.length > 0 && (
        <div className="flex flex-wrap gap-2 pt-1 border-t border-slate-100">
          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-slate-500">
            <Tag className="w-3 h-3" /> Custom tags
          </span>
          {customSelected.map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-violet-100 text-violet-700 border border-violet-200"
            >
              {labelForCategory(tag)}
              <button
                type="button"
                onClick={() => toggle(tag)}
                className="text-violet-500 hover:text-violet-700"
                aria-label={`Remove ${tag}`}
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Custom tag input */}
      {allowCustom && (
        <div className="flex items-center gap-2">
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Add custom category (press Enter)"
            className="flex-1 text-xs border border-slate-300 rounded-md px-2.5 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-amber-500"
            data-testid={`${testId}-custom-input`}
            maxLength={40}
          />
          <button
            type="button"
            onClick={addCustom}
            disabled={!draft.trim()}
            className="text-xs font-semibold px-3 py-1.5 rounded-md bg-violet-50 text-violet-700 border border-violet-200 hover:bg-violet-100 disabled:opacity-50"
          >
            Add tag
          </button>
        </div>
      )}
    </div>
  );
}
